import {
  Menu,
  MenuItem,
  Button as MUIButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

export type FilterOption<T = string> = {
  value: T;
  label: string;
  count?: number;
};

type FilterDropdownButtonProps<T = string> = {
  options: FilterOption<T>[];
  selectedValue: T;
  onSelectionChange: (value: T) => void;
  placeholder?: string;
};

export const FilterDropdownButton = <T = string>({
  options,
  selectedValue,
  onSelectionChange,
  placeholder,
}: FilterDropdownButtonProps<T>) => {
  const { colors, customSpacing, radius, customFontWeight } = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (value: T) => {
    onSelectionChange(value);
    handleClose();
  };

  const selectedOption = options.find(
    (option) => option.value === selectedValue,
  );
  const displayText = selectedOption?.label || placeholder || "Select...";

  return (
    <>
      <MUIButton
        variant="outlined"
        onClick={handleClick}
        endIcon={<ChevronDown size={16} />}
        sx={{
          backgroundColor: colors.background.primary,
          border: `1px solid ${colors.border.primary}`,
          borderRadius: radius.lg,
          color: colors.text.primary,
          fontWeight: customFontWeight.medium,
          textTransform: "none",
          px: customSpacing.lg,
          py: customSpacing.md,
          minWidth: 160,
          justifyContent: "space-between",
          "&:hover": {
            backgroundColor: colors.background.hover,
            borderColor: colors.border.secondary,
          },
          "&:focus": {
            outline: "none",
            borderColor: colors.border.brand,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            variant="body2"
            sx={{ fontWeight: customFontWeight.medium }}
          >
            {displayText}
          </Typography>
          {selectedOption?.count !== undefined && (
            <Typography
              variant="caption"
              sx={{
                backgroundColor: colors.background.secondary,
                color: colors.text.secondary,
                px: customSpacing.xs,
                borderRadius: radius.sm,
                fontWeight: customFontWeight.medium,
              }}
            >
              {selectedOption.count}
            </Typography>
          )}
        </Stack>
      </MUIButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: colors.background.primary,
            border: `1px solid ${colors.border.primary}`,
            borderRadius: radius.lg,
            minWidth: 160,
            mt: 1,
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={String(option.value)}
            onClick={() => handleSelect(option.value)}
            selected={option.value === selectedValue}
            sx={{
              px: customSpacing.lg,
              py: customSpacing.md,
              "&:hover": {
                backgroundColor: colors.background.hover,
              },
              "&.Mui-selected": {
                backgroundColor: colors.background.hover,
              },
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.primary,
                  fontWeight: customFontWeight.medium,
                }}
              >
                {option.label}
              </Typography>
              {option.count !== undefined && (
                <Typography
                  variant="caption"
                  sx={{
                    backgroundColor: colors.background.secondary,
                    color: colors.text.secondary,
                    px: customSpacing.xs,
                    borderRadius: radius.sm,
                    fontWeight: customFontWeight.medium,
                  }}
                >
                  {option.count}
                </Typography>
              )}
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
